import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import './UniversalProductLibrary.css';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

// SVG Icons
const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const ImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

export default function SitemapImport({ onClose, onImportComplete, categories, countries }) {
  const { getAccessTokenSilently } = useAuth0();
  const notification = useNotification();
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const [sitemapUrl, setSitemapUrl] = useState('');
  const [category, setCategory] = useState('');
  const [country, setCountry] = useState('CA');
  const [brand, setBrand] = useState('');
  const [removeBackground, setRemoveBackground] = useState(true);

  // Preview state
  const [previewProducts, setPreviewProducts] = useState([]);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');

  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const fetchPreview = async () => {
    if (!sitemapUrl.trim()) {
      setPreviewError('Please enter a sitemap URL');
      return;
    }

    setIsPreviewing(true);
    setPreviewError('');
    setPreviewProducts([]);
    setImportResults(null);

    try {
      // Fetch the sitemap XML directly from the browser to preview
      const response = await axios.get(sitemapUrl, { timeout: 30000 });
      const xml = response.data;

      // Parse product entries
      const urlBlocks = xml.match(/<url>[\s\S]*?<\/url>/g) || [];
      const products = [];

      for (const block of urlBlocks) {
        const locMatch = block.match(/<loc>(.*?)<\/loc>/);
        const imageLoc = block.match(/<image:loc>(.*?)<\/image:loc>/);
        const imageTitle = block.match(/<image:title>(.*?)<\/image:title>/);

        if (!locMatch) continue;
        const url = locMatch[1];
        if (!url.includes('/products/')) continue;

        const handle = url.split('/products/')[1];
        if (!handle) continue;

        let name = imageTitle
          ? imageTitle[1].trim()
          : handle.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

        name = name.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#39;/g, "'").replace(/&quot;/g, '"');

        let imageUrl = imageLoc ? imageLoc[1].replace(/&amp;/g, '&') : null;
        if (imageUrl && imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;

        products.push({ name, handle, imageUrl, sourceUrl: url });
      }

      if (products.length === 0) {
        setPreviewError('No products found in this sitemap. Make sure the URL points to a product sitemap with <image:image> tags.');
      } else {
        setPreviewProducts(products);
      }
    } catch (error) {
      if (error.response) {
        setPreviewError(`Failed to fetch sitemap (HTTP ${error.response.status}). Check the URL and try again.`);
      } else if (error.code === 'ERR_NETWORK') {
        setPreviewError('Network error — the sitemap may be blocked by CORS. The import will still work since it fetches from the server.');
        // Try to let the user proceed anyway by showing a message
      } else {
        setPreviewError(`Failed to fetch sitemap: ${error.message}`);
      }
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!category) {
      notification.error('Please select a category');
      return;
    }

    setIsImporting(true);
    setImportResults(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await axios.post(
        `${API_BASE}/api/universal-products/admin/import-sitemap`,
        {
          sitemapUrl: sitemapUrl.trim(),
          category,
          country,
          brand: brand.trim() || undefined,
          removeBackground,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 1800000, // 30 min — large sitemaps with BG removal take time
        }
      );

      if (response.data.created > 0) {
        const skippedMsg = response.data.skipped ? ` (${response.data.skipped} duplicates skipped)` : '';
        notification.success(`Imported ${response.data.created} products from sitemap${skippedMsg}`);
      } else if (response.data.skipped > 0) {
        notification.info(`All ${response.data.skipped} products already exist — nothing new to import`);
      }
      if (response.data.errors > 0) {
        notification.warning(`${response.data.errors} products had errors during import`);
      }

      // Only update local state if still mounted
      if (mountedRef.current) {
        setImportResults(response.data);
      }
      // Always trigger refresh callback (parent may still be mounted)
      if (onImportComplete) onImportComplete();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to import from sitemap';
      notification.error(msg);
      if (mountedRef.current) {
        setImportResults({ error: msg });
      }
    } finally {
      if (mountedRef.current) {
        setIsImporting(false);
      }
    }
  };

  const productsWithImages = previewProducts.filter(p => p.imageUrl);

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="upl-subpage-header">
        <div className="upl-subpage-header-left">
          <button className="upl-back-btn" onClick={onClose}>
            <ArrowLeftIcon />
          </button>
          <div>
            <h1 className="upl-subpage-title">Import from Sitemap</h1>
            <p className="upl-subpage-subtitle">Import products from a website's product sitemap XML</p>
          </div>
        </div>
        <div className="upl-subpage-actions">
          <button className="upl-btn outline-gray" onClick={onClose}>
            {isImporting ? 'Close' : 'Cancel'}
          </button>
          <button
            className="upl-btn primary"
            onClick={handleImport}
            disabled={isImporting || !sitemapUrl.trim() || !category}
          >
            <DownloadIcon />
            {isImporting ? 'Importing...' : `Import${previewProducts.length > 0 ? ` (${previewProducts.length})` : ''}`}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="upl-scroll-content">
        <div className="upl-bulk-content">
          {/* Instructions */}
          <div className="upl-instructions">
            <h3>How it works</h3>
            <ul>
              <li>Paste a product sitemap URL (usually from Shopify, WooCommerce, or similar)</li>
              <li>The sitemap is parsed for product titles and images</li>
              <li>Images are uploaded to S3 and backgrounds are removed automatically</li>
              <li>Products are created in the universal product library</li>
            </ul>
          </div>

          {/* Sitemap URL Input */}
          <div className="upl-json-section">
            <div className="upl-json-header">
              <h3 className="upl-json-title">Sitemap URL</h3>
              <button
                className="upl-btn outline"
                onClick={fetchPreview}
                disabled={isPreviewing || !sitemapUrl.trim()}
              >
                {isPreviewing ? 'Loading...' : 'Preview'}
              </button>
            </div>
            <input
              type="url"
              className="upl-filter-input"
              placeholder="https://example.com/sitemap_products_1.xml"
              value={sitemapUrl}
              onChange={(e) => {
                setSitemapUrl(e.target.value);
                setPreviewProducts([]);
                setPreviewError('');
                setImportResults(null);
              }}
              style={{ width: '100%', padding: '12px', fontSize: '14px' }}
            />
          </div>

          {/* Settings */}
          <div className="upl-json-section">
            <h3 className="upl-json-title" style={{ marginBottom: 16 }}>Import Settings</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="upl-filter-group">
                <label>Category *</label>
                <select
                  className="upl-filter-input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="upl-filter-group">
                <label>Country *</label>
                <select
                  className="upl-filter-input"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="upl-filter-group">
                <label>Brand</label>
                <input
                  type="text"
                  className="upl-filter-input"
                  placeholder="e.g. 49th Parallel Coffee Roasters"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                />
              </div>
              <div className="upl-filter-group">
                <label>Background Removal</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <input
                    type="checkbox"
                    id="removeBg"
                    checked={removeBackground}
                    onChange={(e) => setRemoveBackground(e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <label htmlFor="removeBg" style={{ fontWeight: 'normal', fontSize: 14, color: '#374151' }}>
                    Remove image backgrounds
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Error */}
          {previewError && (
            <div className="upl-validation-errors">
              <AlertCircleIcon />
              <div>
                <h3>Preview Issue</h3>
                <p>{previewError}</p>
              </div>
            </div>
          )}

          {/* Preview Results */}
          {previewProducts.length > 0 && (
            <div className="upl-json-section">
              <div className="upl-json-header">
                <h3 className="upl-json-title">
                  Found {previewProducts.length} products
                  {productsWithImages.length > 0 && (
                    <span style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 14, marginLeft: 8 }}>
                      ({productsWithImages.length} with images)
                    </span>
                  )}
                </h3>
              </div>
              <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <table className="upl-table" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Image</th>
                      <th>Product Name</th>
                      <th>Handle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewProducts.map((product, i) => (
                      <tr key={i}>
                        <td>
                          {product.imageUrl ? (
                            <img
                              src={product.imageUrl}
                              alt={product.name}
                              style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, background: '#f9fafb' }}
                            />
                          ) : (
                            <div style={{ width: 40, height: 40, background: '#f3f4f6', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                              <ImageIcon />
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: 500 }}>{product.name}</td>
                        <td style={{ color: '#6b7280', fontSize: 13 }}>{product.handle}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Import Progress */}
          {isImporting && (
            <div className="upl-validation-success" style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="sitemap-spinner" />
                <div>
                  <h3 style={{ color: '#1e40af' }}>Importing products...</h3>
                  <p style={{ color: '#3b82f6' }}>
                    Uploading images to S3{removeBackground ? ' and removing backgrounds' : ''}. This may take a few minutes for large catalogs.
                    You can close this screen — you'll get a notification when it's done.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Import Results */}
          {importResults && !importResults.error && (
            <div className="upl-validation-success">
              <CheckCircleIcon />
              <div>
                <h3>Import Complete!</h3>
                <p>
                  Created {importResults.created} product{importResults.created !== 1 ? 's' : ''}
                  {importResults.skipped > 0 && `, ${importResults.skipped} duplicates skipped`}
                  {importResults.errors > 0 && `, ${importResults.errors} errors`}
                  {' '}out of {importResults.total} found in sitemap.
                </p>
                {importResults.details?.errors?.length > 0 && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', color: '#dc2626' }}>View errors</summary>
                    <ul style={{ marginTop: 4, fontSize: 13 }}>
                      {importResults.details.errors.map((err, i) => (
                        <li key={i}>{err.name}: {err.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}

          {importResults && importResults.error && (
            <div className="upl-validation-errors">
              <AlertCircleIcon />
              <div>
                <h3>Import Failed</h3>
                <p>{importResults.error}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .sitemap-spinner {
          width: 24px;
          height: 24px;
          border: 3px solid #bfdbfe;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: sitemap-spin 0.8s linear infinite;
        }
        @keyframes sitemap-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
